import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Code2, Copy, Inbox, Play, Search, Text, X } from 'lucide-react';
import { ProjectTag, ScopeBar } from '../components/scope/ScopeBar';
import { IngestionPill, isMoving } from '../components/feed/IngestionPill';
import { Journey } from '../components/feed/Journey';
import { SpaceFilesFeed } from '../components/feed/SpaceFilesFeed';
import { SourceIcon, sourceStyle } from '../components/run/SourceIcon';
import { Input, Select } from '../components/ui/input';
import { Markdown } from '../components/ui/markdown';
import { ResizeHandle } from '../components/ui/resize-handle';
import { Snippet } from '../components/ui/snippet';
import { api, type FeedIngestion, type FeedItem, type FeedSearchHit, type FeedSearchResult } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useResizablePanel } from '../lib/resizable';
import { useScope } from '../lib/scope';
import { formatSource, useSourceLabels } from '../lib/source-label';
import { cn, formatDate, timeAgo } from '../lib/utils';

const SOURCE_TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'SLACK', label: 'Slack' },
  { value: 'GRANOLA', label: 'Granola' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'DRIVE_FILE', label: 'Drive' },
  // Documents that came with something else — linked in a message, or mailed as an attachment.
  // Worth its own tab for the reason it is its own source type: it is the material nobody curated.
  { value: 'LINKED_DOCUMENT', label: 'Attached' },
  // People's edits to documents automations filed in Drive — what the automations got corrected on.
  { value: 'AUTOMATION_EDIT', label: 'Brief edits' },
  { value: 'FILE_UPLOAD', label: 'File Upload' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'MCP', label: 'MCP Agent' },
  { value: 'CLI_IMPORT', label: 'CLI Import' },
];

type ContentView = 'rendered' | 'raw';

/**
 * Which of the two readings of an item's content is showing.
 *
 * Persisted because it is a standing preference rather than a per-item choice: someone who wants to
 * see the bytes as captured wants that for the next item too, and re-picking it on every click is how
 * a toggle stops being used at all.
 */
const VIEW_KEY = 'cdr.feed.view';

function readView(): ContentView {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'raw' ? 'raw' : 'rendered';
  } catch {
    return 'rendered';
  }
}

/** A string field of an item's metadata, when it holds one — the JSON column types every key as unknown. */
function metaString(item: FeedItem, key: string): string | null {
  const value = item.metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * What to call an item in a list of them.
 *
 * A Granola note carries its meeting title, which is the only name anyone would recognise it by;
 * everything else is named by where it arrived through — the folder or channel, resolved to whatever
 * it is currently called rather than left as the raw `slack:C08ENG` the row stores.
 */
function itemTitle(item: FeedItem, labels: Record<string, string>): string {
  const place = formatSource(item.source, labels);
  return metaString(item, 'title') ?? (place || item.sourceType);
}

/**
 * The windows the search offers, and why 30 days is the default.
 *
 * These are the automation agent's own numbers, not a UI preference. `feed_search` defaults to 30
 * days and its schema caps `days` at 90, so a search here over those windows is answerable by a run
 * — which is the point of the view. "All time" is offered beyond them because an auditor's first
 * question is often whether an item exists at all, and the answer "not in the last 90 days" is not
 * the same as "not captured"; it is labelled so nobody reads a hit outside 90 days as something an
 * agent could have reached.
 */
const AGENT_DEFAULT_DAYS = 30;
const AGENT_MAX_DAYS = 90;
/** What one `feed_search` call returns per question by default — where a run's attention stopped. */
const AGENT_DEFAULT_LIMIT = 10;

const SEARCH_WINDOWS: { value: number | ''; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: AGENT_DEFAULT_DAYS, label: 'Last 30 days · agent default' },
  { value: AGENT_MAX_DAYS, label: 'Last 90 days · agent maximum' },
  { value: '', label: 'All time · beyond agent reach' },
];

/**
 * The first line with anything in it, with markdown's leading marks taken off.
 *
 * A row is one line high, so the useful thing to put in it is the first sentence of the content —
 * except that a Granola note opens with `# Meeting title` and a Slack thread often with a bullet,
 * which would make every preview begin with punctuation.
 */
function preview(content: string): string {
  for (const line of content.split('\n')) {
    const text = line.replace(/^\s*(?:#{1,6}|[-*+>]|\d+[.)])\s*/, '').trim();
    if (text) return text;
  }
  return '';
}

type FeedView = 'captured' | 'spaces';

/** Each view's own URL keys: carried into the other view, they would name something it cannot hold. */
const VIEW_KEYS: Record<FeedView, string[]> = { captured: ['item', 'q'], spaces: ['view', 'file', 'status'] };

/**
 * The page has two views: what the knowledge base received (`CapturedFeed`) and, for someone with
 * personal spaces, the files in theirs and where each is in being indexed (`SpaceFilesFeed`). Two
 * components rather than one with a mode, so neither view's polling or fetching runs while the other
 * is showing. The view is in the URL (`?view=spaces`); the project scope is not, so it survives the switch.
 */
export default function Feed() {
  const { hasFeature } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const spaces = hasFeature('SPACES');
  const view: FeedView = spaces && searchParams.get('view') === 'spaces' ? 'spaces' : 'captured';

  const choose = (next: FeedView) => {
    if (next === view) return;
    const params = new URLSearchParams(searchParams);
    for (const key of VIEW_KEYS[view]) params.delete(key);
    if (next === 'spaces') params.set('view', 'spaces');
    setSearchParams(params);
  };
  const viewSwitch = spaces ? <FeedViewSwitch view={view} onChange={choose} /> : null;

  return view === 'spaces' ? <SpaceFilesFeed viewSwitch={viewSwitch} /> : <CapturedFeed viewSwitch={viewSwitch} />;
}

function FeedViewSwitch({ view, onChange }: { view: FeedView; onChange: (view: FeedView) => void }) {
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-border bg-surface-subtle p-0.5" role="tablist">
      {(
        [
          ['captured', 'Captured content', 'What the knowledge base received, and what became of it'],
          ['spaces', 'Space files', 'Files in your space and shared with you, and whether each has been read for search'],
        ] as const
      ).map(([value, label, title]) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={view === value}
          onClick={() => onChange(value)}
          title={title}
          className={cn(
            'flex-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
            view === value ? 'bg-card text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * Everything captured, newest first, across every project — a list beside the item it opens.
 *
 * Org-level rather than per-project because that is the question the feed answers: content now arrives
 * from sources with no stated destination, and the router may file one meeting in three projects — so
 * "what came in, and where did it go" is inherently a view over the whole tree. Narrowing to one
 * project is a filter, not a different page.
 *
 * Two panes rather than a column of cards, the shell Ask KB, the review queue and the sweeps console
 * share. The list was previously a stack of rows each carrying its own scrolling excerpt of the
 * content, which is the worst of both: too much to scan past and too little to read, with a nested
 * scrollbar per row. So the row is now one line about the item and the pane beside it is the item —
 * whole, in the width the window actually has, and in either of the two readings that matter for
 * content whose whole point is that it is raw.
 */
function CapturedFeed({ viewSwitch }: { viewSwitch: ReactNode }) {
  const scope = useScope();
  const sourceLabels = useSourceLabels();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sourceType, setSourceType] = useState('');
  const [source, setSource] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ContentView>(readView);
  const [copied, setCopied] = useState(false);
  /** An item the URL names that the current filters don't hold — fetched so a link always opens. */
  const [fetched, setFetched] = useState<FeedItem | null>(null);
  // Search state. The query lives in the URL beside the selected item, so a search someone ran while
  // auditing is a link they can paste into a ticket rather than a set of steps to repeat.
  const [draft, setDraft] = useState(() => searchParams.get('q') ?? '');
  const [days, setDays] = useState<number | ''>(AGENT_DEFAULT_DAYS);
  const [result, setResult] = useState<FeedSearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const query = { projectId: scope.projectId, scope: scope.mode };
  const activeQuery = (searchParams.get('q') ?? '').trim();
  const searchMode = activeQuery.length > 0;

  // Which item the right pane holds, in the URL so one can be linked to and the back button means
  // something — the same place the sweeps console keeps its selection.
  const selectedId = searchParams.get('item');
  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? (fetched && fetched.id === selectedId ? fetched : null),
    [items, fetched, selectedId],
  );

  const select = useCallback(
    (id: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (id) params.set('item', id);
      else params.delete('item');
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /**
   * Items still on their way in — waiting, queued, being written — are re-checked every ten seconds,
   * so a row turns from "Processing" to "In memory" in front of the person waiting for it. Only those
   * ids are asked about, and nothing is polled once every visible item has settled.
   */
  const movingIds = useMemo(() => {
    const visible: Array<{ id: string; ingestion?: FeedIngestion }> = searchMode ? result?.hits ?? [] : items;
    return visible.filter((item) => isMoving(item.ingestion)).map((item) => item.id).slice(0, 100);
  }, [items, result, searchMode]);
  const movingKey = movingIds.join(',');
  const applyStates = useCallback((states: Record<string, FeedIngestion>) => {
    const patch = <T extends { id: string; ingestion?: FeedIngestion }>(item: T): T =>
      states[item.id] ? { ...item, ingestion: states[item.id] } : item;
    setItems((current) => current.map(patch));
    setResult((current) => (current ? { ...current, hits: current.hits.map(patch) } : current));
    setFetched((current) => (current ? patch(current) : current));
  }, []);
  useEffect(() => {
    if (!movingKey) return;
    const timer = window.setInterval(() => {
      api.feed
        .status(movingKey.split(','))
        .then(applyStates)
        .catch(() => {});
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [movingKey, applyStates]);

  /**
   * Process now, for one waiting item: takes it out of its batch and starts a run for it alone, leaving
   * the rest of the window where it was. The new state is fetched straight away so the pill turns to
   * Queued on the press rather than at the next ten-second poll.
   */
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [processError, setProcessError] = useState<string | null>(null);
  useEffect(() => setProcessError(null), [selectedId]);
  const processNow = async (id: string) => {
    setProcessingId(id);
    setProcessError(null);
    try {
      await api.kb.processFeedItem(id);
    } catch (err) {
      setProcessError(err instanceof Error ? err.message : 'Could not process this item');
    }
    try {
      applyStates(await api.feed.status([id]));
    } catch {
      // The poll will catch up.
    } finally {
      setProcessingId(null);
    }
  };

  /** Debounced so typing a question is one search rather than one per keystroke. */
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = draft.trim();
      if (next === activeQuery) return;
      const params = new URLSearchParams(searchParams);
      if (next) params.set('q', next);
      else params.delete('q');
      setSearchParams(params, { replace: true });
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, activeQuery]);

  // The same filters the listing uses are passed through, because they are filters the agent's tool
  // takes too — narrowing to Slack here is narrowing to Slack there, and a view that quietly
  // searched everything while showing a source-type pill would be the wrong kind of convincing.
  useEffect(() => {
    if (!searchMode) {
      setResult(null);
      setSearching(false);
      return;
    }
    let alive = true;
    setSearching(true);
    setError(null);
    api.feed
      .search({
        ...query,
        q: activeQuery,
        sourceType: sourceType || undefined,
        source: source || undefined,
        days: days === '' ? undefined : days,
        limit: 50,
      })
      .then((r) => alive && setResult(r))
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        setResult(null);
      })
      .finally(() => alive && setSearching(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode, activeQuery, scope.projectId, scope.mode, sourceType, source, days]);

  const chooseView = useCallback((next: ContentView) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // A private-mode browser refusing storage is not a reason to break the toggle.
    }
  }, []);

  const listPanel = useResizablePanel({
    initial: 400,
    min: 300,
    max: 620,
    side: 'left',
    storageKey: 'feed.list.width',
  });

  useEffect(() => {
    api.feed.sources(query).then(setSources).catch(() => setSources([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.projectId, scope.mode]);

  // A source chosen under one scope may not exist under the next — silently returning nothing would
  // read as an empty feed rather than as a stale filter.
  useEffect(() => {
    if (source && sources.length > 0 && !sources.includes(source)) setSource('');
  }, [sources, source]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.feed
      .list({ ...query, sourceType: sourceType || undefined, source: source || undefined, limit: 30 })
      .then((page) => {
        setItems(page.items);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.projectId, scope.mode, sourceType, source]);

  const loadMore = useCallback(() => {
    // A search is a ranked top-N, not a cursored page: paging the *listing* in behind it would fetch
    // rows nothing renders and leave `items` holding results for a query nobody asked.
    if (searchMode) return;
    if (!hasMore || loading || loadingMore || !cursor) return;
    setLoadingMore(true);
    api.feed
      .list({ ...query, sourceType: sourceType || undefined, source: source || undefined, cursor, limit: 30 })
      .then((page) => {
        setItems((prev) => [...prev, ...page.items]);
        setCursor(page.nextCursor);
        setHasMore(page.nextCursor !== null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoadingMore(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.projectId, scope.mode, sourceType, source, cursor, hasMore, loading, loadingMore, searchMode]);

  // Rooted on the list's own scroller rather than the viewport: the list scrolls inside the pane now,
  // so a viewport-rooted margin would be measured against a box the sentinel never moves through.
  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollerRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  // A link into the feed names an item the filters may not reach — a Slack message while the page is
  // showing Granola, or something older than the pages loaded so far. Fetching it means the pane shows
  // what the link pointed at instead of an empty state that reads as a dead link.
  useEffect(() => {
    if (!selectedId) {
      setFetched(null);
      return;
    }
    if (items.some((i) => i.id === selectedId) || fetched?.id === selectedId) return;
    let alive = true;
    api.feed
      .get(selectedId)
      .then((item) => alive && setFetched(item))
      .catch(() => alive && setFetched(null));
    return () => {
      alive = false;
    };
  }, [selectedId, items, fetched]);

  useEffect(() => setCopied(false), [selectedId]);

  const selectedProject = selected ? scope.projectFor(selected.projectId) : null;
  const showProject = !(!scope.isEverything && scope.mode === 'self');

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      {/* Flush to the app chrome on both sides and to the window below: the feed is a reading surface,
          and a centered column inside a page that is already inside a shell is two frames too many. */}
      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full flex overflow-hidden">
        <aside
          style={{ width: listPanel.width }}
          className="flex h-full flex-shrink-0 flex-col border-r border-border bg-card"
        >
          <div className="border-b border-border-subtle px-4 pt-5 pb-3">
            <div className="flex items-baseline gap-2">
              <h1 className="text-lg font-bold text-ink">Feed</h1>
              <span className="ml-auto text-[10px] tabular-nums text-ink-faint">
                {searchMode ? `${result?.hits.length ?? 0} matched` : `${items.length} shown`}
              </span>
            </div>
            {viewSwitch && <div className="mt-2">{viewSwitch}</div>}
            <p className={cn('text-[11px] leading-relaxed text-ink-muted', viewSwitch ? 'mt-2' : 'mt-1')}>
              {searchMode
                ? // Said plainly, because it is the whole claim of this view: an auditor has to know
                  // they are looking at the run's own retrieval and not at a second search that
                  // merely resembles it.
                  'Searching exactly as an automation run does — the same match, ranking and extract its feed_search returns.'
                : scope.isEverything
                  ? 'Raw content as captured, across every project — messages, meeting notes, uploads and manual entries.'
                  : `Raw content captured in ${scope.project?.name ?? 'this project'}${
                      scope.mode === 'cone' ? ' and its sub-projects' : ''
                    }.`}
            </p>

            <div className="mt-3 space-y-2">
              <ScopeBar scope={scope} />

              {/* The agent's own search, run by hand. It sits above the filters rather than beside
                  them because it changes what the list *is* — ranked hits instead of a timeline —
                  and the filters below then narrow whichever of the two is showing. */}
              <div className="relative">
                <Search size={13} className="pointer-events-none absolute left-2.5 top-2 text-ink-faint" />
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Search captured content, as the agent does"
                  aria-label="Search the feed the way an automation's feed_search does"
                  className="w-full py-1.5 pl-8 pr-7 text-xs"
                />
                {draft && (
                  <button
                    type="button"
                    onClick={() => setDraft('')}
                    aria-label="Clear the search"
                    className="absolute right-2 top-2 text-ink-faint transition-colors hover:text-ink"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>

              {searchMode && (
                <>
                  <Select
                    value={String(days)}
                    onChange={(e) => setDays(e.target.value === '' ? '' : Number(e.target.value))}
                    className="w-full py-1.5 text-xs"
                    title="How far back to search. The agent's own tool defaults to 30 days and cannot ask for more than 90."
                  >
                    {SEARCH_WINDOWS.map((w) => (
                      <option key={String(w.value)} value={String(w.value)}>
                        {w.label}
                      </option>
                    ))}
                  </Select>

                  {/* The line that makes this an audit rather than a search box. The match is a
                      disjunction, so hits are almost never zero — an item sharing one content word
                      comes back. "Contain every term" is the number that answers whether anything
                      actually discussed this, and stating it here is cheaper than explaining later
                      why forty hits meant nothing. */}
                  {result && !searching && (
                    <p className="text-[11px] leading-relaxed text-ink-muted">
                      <span className="font-medium text-ink">{result.matchCount}</span> matched ·{' '}
                      <span className={cn('font-medium', result.strongMatchCount > 0 ? 'text-ink' : 'text-ink-faint')}>
                        {result.strongMatchCount}
                      </span>{' '}
                      contain every term
                      {result.matchCount > 0 && result.strongMatchCount === 0 && (
                        <span className="text-ink-faint"> — these only share a word with the question</span>
                      )}
                      {result.matchCount > AGENT_DEFAULT_LIMIT && (
                        <span className="text-ink-faint">
                          . A run reads the top {AGENT_DEFAULT_LIMIT} of these per question.
                        </span>
                      )}
                    </p>
                  )}
                </>
              )}

              <Select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full py-1.5 text-xs"
                title="Narrow to one folder or channel"
              >
                <option value="">All sources</option>
                {sources.map((s) => (
                  <option key={s} value={s}>
                    {formatSource(s, sourceLabels)}
                  </option>
                ))}
              </Select>
              {/* Pills rather than the underlined tab row this was: seven of them do not fit across a
                  pane you can drag down to 300px, and a tab strip that wraps stops reading as tabs. */}
              <div className="flex flex-wrap gap-1">
                {SOURCE_TYPE_TABS.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setSourceType(t.value)}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
                      sourceType === t.value
                        ? 'border-orange-500 bg-orange-500 text-white'
                        // A hair off-white rather than `bg-card`: the pane is white now, and seven
                        // white-on-white pills read as loose words rather than as one set of choices.
                        : 'border-border bg-surface-subtle text-ink-muted hover:border-orange-300 hover:text-ink',
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div className="border-b border-danger-ring bg-danger-bg px-4 py-2.5 text-[11px] text-danger-text">
              {error}
            </div>
          )}

          <div ref={scrollerRef} className="flex-1 overflow-y-auto">
            {searchMode ? (
              searching ? (
                <p className="px-4 py-12 text-center text-[11px] text-ink-faint">Searching…</p>
              ) : !result || result.hits.length === 0 ? (
                <div className="px-4 py-12 text-center text-[11px] text-ink-faint">
                  <Search size={18} className="mx-auto mb-2 opacity-60" />
                  Nothing captured matches that
                  {/* Worth saying, because the window is the usual reason and it is one click away —
                      and because a run asking the same question would have found nothing either. */}
                  {days !== '' && <span className="mt-1 block">Try a wider window.</span>}
                </div>
              ) : (
                <div className="divide-y divide-border-subtle">
                  {result.hits.map((hit, index) => (
                    <SearchHitRow
                      key={hit.id}
                      hit={hit}
                      rank={index + 1}
                      labels={sourceLabels}
                      project={showProject ? scope.projectFor(hit.projectId) : null}
                      active={hit.id === selectedId}
                      onOpen={() => select(hit.id)}
                    />
                  ))}
                </div>
              )
            ) : loading ? (
              <p className="px-4 py-12 text-center text-[11px] text-ink-faint">Loading…</p>
            ) : items.length === 0 ? (
              <div className="px-4 py-12 text-center text-[11px] text-ink-faint">
                <Inbox size={18} className="mx-auto mb-2 opacity-60" />
                No feed items found
              </div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {items.map((item) => (
                  <FeedRow
                    key={item.id}
                    item={item}
                    labels={sourceLabels}
                    project={showProject ? scope.projectFor(item.projectId) : null}
                    active={item.id === selectedId}
                    onOpen={() => select(item.id)}
                  />
                ))}
              </div>
            )}

            {!searchMode && !loading && items.length > 0 && (
              <div ref={sentinelRef} className="py-4 text-center text-[10px] text-ink-faint">
                {loadingMore ? 'Loading more…' : hasMore ? '' : 'End of feed'}
              </div>
            )}
          </div>
        </aside>

        {/* Carries the pane colour itself: left transparent it would show 5px of the app's warm
            page background as a stripe between two white panes, which is the seam this layout is
            meant not to have. The hover highlight is drawn inside it and still reads. */}
        <ResizeHandle handleProps={listPanel.handleProps} onDoubleClick={listPanel.reset} className="bg-card" />

        <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-card">
          {selected ? (
            <>
              {/* Outside the scroller rather than sticky inside it, so the toggle and the copy stay
                  put through a 100k-character meeting note instead of riding a blur over the text. */}
              <header className="flex-shrink-0 border-b border-border px-7 py-4">
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate text-sm font-semibold text-ink">
                      {itemTitle(selected, sourceLabels)}
                    </h2>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <SourceLabel sourceType={selected.sourceType} metadata={selected.metadata} />
                      <IngestionPill ingestion={selected.ingestion} />
                      {/* Which project absorbed it. Linked, because "where did this land" is usually
                          followed by "show me". */}
                      {showProject && selectedProject && (
                        <Link to={`/p/${selectedProject.slug}/kb`} className="transition-opacity hover:opacity-80">
                          <ProjectTag name={selectedProject.name} />
                        </Link>
                      )}
                      <span className="truncate text-[11px] text-ink-muted">
                        {formatSource(selected.source, sourceLabels)}
                      </span>
                      <span className="text-[11px] text-ink-faint">{formatDate(selected.capturedAt)}</span>
                    </div>
                    {processError && <p className="mt-1.5 text-[11px] text-danger-text">{processError}</p>}
                  </div>

                  <div className="flex flex-shrink-0 items-center gap-2">
                    {/* Only while it waits for a batch window: once queued or running there is
                        nothing to bring forward. */}
                    {selected.ingestion?.state === 'waiting' && (
                      <button
                        type="button"
                        onClick={() => processNow(selected.id)}
                        disabled={processingId !== null}
                        title="Take this item out of its batch and absorb it now. The rest of the batch keeps its window."
                        className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-medium text-ink-muted transition-colors hover:border-border-strong hover:text-ink disabled:opacity-50"
                      >
                        <Play size={12} />
                        {processingId === selected.id ? 'Starting…' : 'Process now'}
                      </button>
                    )}
                    <div className="flex items-center gap-0.5 rounded-md border border-border bg-card p-0.5">
                      {(
                        [
                          ['rendered', 'Rendered', Text],
                          ['raw', 'Raw', Code2],
                        ] as const
                      ).map(([value, label, Icon]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => chooseView(value)}
                          title={
                            value === 'raw'
                              ? 'The text exactly as it was captured'
                              : 'The same text with its markdown rendered'
                          }
                          className={cn(
                            'flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                            view === value
                              ? 'bg-surface-muted text-ink'
                              : 'text-ink-muted hover:text-ink',
                          )}
                        >
                          <Icon size={11} />
                          {label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(selected.content);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      }}
                      title="Copy the content as captured"
                      className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-[11px] font-medium text-ink-muted transition-colors hover:border-border-strong hover:text-ink"
                    >
                      {copied ? (
                        <>
                          <Check size={12} className="text-green-500" />
                          Copied
                        </>
                      ) : (
                        <>
                          <Copy size={12} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </header>

              <div className="flex-1 overflow-y-auto">
                <div className="px-7 py-6">
                  {view === 'raw' ? (
                    // Whitespace kept and monospaced: in this reading the point is what was actually
                    // stored, so a run of blank lines or a stray indent has to survive being shown.
                    <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-ink-secondary">
                      {selected.content}
                    </pre>
                  ) : (
                    <Markdown>{selected.content}</Markdown>
                  )}

                  {/* Under the content: read the item first, then see what it became. The status
                      pill in the header still answers "did it get in" without a scroll. */}
                  <div className="mt-10">
                    <Journey feedItemId={selected.id} ingestion={selected.ingestion} project={selectedProject} />
                  </div>

                  {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                    <details className="mt-10 border-t border-border-subtle pt-4">
                      <summary className="cursor-pointer text-[11px] font-medium text-ink-muted hover:text-ink">
                        Metadata
                      </summary>
                      <pre className="mt-2 overflow-x-auto rounded-md border border-border-subtle bg-surface-subtle p-3 font-mono text-[11px] text-ink-secondary">
                        {JSON.stringify(selected.metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center px-8">
              <div className="text-center">
                <Inbox size={22} className="mx-auto mb-3 text-ink-faint opacity-60" />
                <p className="text-xs text-ink-muted">
                  {selectedId ? 'That item is no longer in the feed.' : 'Pick something on the left to read it.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One arrival, one line about it.
 *
 * The selected row is marked on its leading edge rather than by a ring or a rounded fill: the rows are
 * flush against one another and against the pane, and anything drawn inside a row's box would put a
 * floating card back into a surface whose whole shape is that it has none.
 */
function FeedRow({
  item,
  labels,
  project,
  active,
  onOpen,
}: {
  item: FeedItem;
  labels: Record<string, string>;
  project: { name: string } | null;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'block w-full border-l-2 px-4 py-3 text-left transition-colors',
        active
          ? 'border-l-orange-500 bg-surface-muted'
          : 'border-l-transparent hover:bg-surface-subtle',
      )}
    >
      <div className="flex items-center gap-2">
        <SourceLabel sourceType={item.sourceType} metadata={item.metadata} />
        {project && <ProjectTag name={project.name} />}
        <span className="ml-auto flex flex-shrink-0 items-center gap-2">
          <IngestionPill ingestion={item.ingestion} />
          <span className="whitespace-nowrap text-[10px] text-ink-faint">{timeAgo(item.capturedAt)}</span>
        </span>
      </div>
      <p className={cn('mt-1.5 truncate text-xs font-medium', active ? 'text-ink' : 'text-ink-secondary')}>
        {itemTitle(item, labels)}
      </p>
      <p className="mt-0.5 truncate text-[11px] text-ink-faint">{preview(item.content)}</p>
    </button>
  );
}

/**
 * One search hit — the same row, plus the three things that only mean something under a query.
 *
 * **The rank is shown** because it is the audit question: a run reads the top ten per question, so
 * whether an item was reachable is a matter of where it landed, not merely whether it came back.
 *
 * **The snippet is not truncated to one line** as the listing's preview is. It is the extract
 * `ts_headline` built around the match, which is the text the agent read when it decided what to
 * open in full — clipping it to a line would show something the run never saw.
 *
 * **"every term" is marked rather than the absence of it**, because the widened match means most
 * rows are the weak kind and badging those would badge nearly everything.
 */
function SearchHitRow({
  hit,
  rank,
  labels,
  project,
  active,
  onOpen,
}: {
  hit: FeedSearchHit;
  rank: number;
  labels: Record<string, string>;
  project: { name: string } | null;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'block w-full border-l-2 px-4 py-3 text-left transition-colors',
        active ? 'border-l-orange-500 bg-surface-muted' : 'border-l-transparent hover:bg-surface-subtle',
      )}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'flex-shrink-0 text-[10px] tabular-nums',
            // Past the tenth, a run would not have read it under the default limit. Faded rather
            // than hidden: "it exists but the agent never reached it" is an answer worth having.
            rank > AGENT_DEFAULT_LIMIT ? 'text-ink-faint/60' : 'text-ink-faint',
          )}
          title={rank > AGENT_DEFAULT_LIMIT ? `Rank ${rank} — beyond the top ${AGENT_DEFAULT_LIMIT} a run reads` : `Rank ${rank}`}
        >
          {rank}
        </span>
        <SourceLabel sourceType={hit.sourceType} metadata={null} />
        {project && <ProjectTag name={project.name} />}
        {hit.allTerms && (
          <span
            className="flex-shrink-0 rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-orange-700"
            title="Contains every content word of the query, not just one"
          >
            every term
          </span>
        )}
        <span className="ml-auto flex flex-shrink-0 items-center gap-2">
          <IngestionPill ingestion={hit.ingestion} />
          <span className="whitespace-nowrap text-[10px] text-ink-faint">{timeAgo(hit.capturedAt)}</span>
        </span>
      </div>
      <p className={cn('mt-1.5 truncate text-xs font-medium', active ? 'text-ink' : 'text-ink-secondary')}>
        {hit.title ?? (formatSource(hit.source, labels) || hit.sourceType)}
      </p>
      {hit.snippet && (
        <p className="mt-1 line-clamp-3 text-[11px] leading-relaxed text-ink-faint">
          <Snippet text={hit.snippet} />
        </p>
      )}
    </button>
  );
}

/** The colored source chip with its name — the same chips the Activity list uses. */
function SourceLabel({ sourceType, metadata }: { sourceType: string; metadata: Record<string, unknown> | null }) {
  const client = typeof metadata?.mcpClient === 'string' ? metadata.mcpClient : null;
  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1.5">
      <SourceIcon sourceType={sourceType} client={client} size="xs" />
      <span className="text-[10px] font-medium text-ink-muted">{sourceStyle(sourceType, client).label}</span>
    </span>
  );
}
